/**
 * DeviceCameraCapture — the in-app LIVE camera modal for a device installation photo.
 *
 * Owner request 2026-09-15: "everyone has a good phone with camera — take a LIVE picture from
 * the tablet, then REVIEW it before adding, or RETAKE." This replaces the old fire-and-upload
 * `<input capture>` flow for people who choose "Take photo".
 *
 * Flow:
 *   live  — full-frame getUserMedia preview + a big shutter (+ front/back switch when >1 camera)
 *   → shutter draws the current frame to a canvas at NATIVE resolution, stops the stream at once,
 *   review — the captured still with "Use photo" and "Retake"
 *   → Use hands the JPEG blob (wrapped as a File) to the parent, which runs it through the SAME
 *     downscale + EXIF-strip + upload path a chosen file uses. Retake re-acquires the stream.
 *
 * Media lifecycle (why there is never a stuck camera light): every exit — shutter, retake, the
 * Escape/✕/overlay close, and component unmount — funnels through stopMediaStream(). The live
 * acquisition lives in an effect whose cleanup stops the stream it started, so React tearing the
 * modal down mid-stream is handled the same as a deliberate close.
 *
 * Fallbacks, never a dead end: the caller only opens this when cameraCaptureSupported() is true,
 * and any getUserMedia rejection here (permission denied, no camera, camera busy) shows a plain
 * sentence plus a "Choose a file instead" button that hands control back to the file input.
 *
 * Design: Meridian tokens; the dark full-frame surface matches the photo viewer. Brand violet on
 * the shutter/primary affordance only (a capture is not an operational state, so the status ramp
 * is untouched). Controls sit along the BOTTOM as large touch targets for one-handed use on a
 * tablet/phone in the field. Motion is CSS and honours prefers-reduced-motion via global.css.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/Button";
import {
  cameraErrorMessage,
  canvasToJpegBlob,
  drawFrameToCanvas,
  frameFileName,
  hasMultipleCameras,
  nextFacingMode,
  stopMediaStream,
  type FacingMode,
} from "@/lib/deviceCamera";

/** Which step the modal is showing. */
type Phase = "live" | "review";

/** A captured still held in memory during review (blob + its object URL for the <img>). */
type Still = { blob: Blob; url: string };

export function DeviceCameraCapture({
  open,
  onClose,
  onCapture,
  onFallbackToFile,
}: {
  /** Controlled open state — the parent owns it so it can also drive the chooser/file input. */
  open: boolean;
  /** Close the modal (Escape, ✕, overlay, or after Use). */
  onClose: () => void;
  /** A confirmed still, ready for the existing upload path. */
  onCapture: (file: File) => void;
  /** Bail out to the file input when the camera can't be used (denied / none / busy). */
  onFallbackToFile: () => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-toast bg-ink/95" />
        <Dialog.Content
          className="fixed inset-0 z-toast flex flex-col bg-ink outline-none"
          aria-describedby={undefined}
        >
          {/* Mount the camera body only while open so unmount cleanup stops the stream. */}
          {open && (
            <CameraBody onClose={onClose} onCapture={onCapture} onFallbackToFile={onFallbackToFile} />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * The stream-holding inner body. Split out so that closing the modal UNMOUNTS it, guaranteeing
 * the acquisition effect's cleanup runs (and the camera turns off) on every close path.
 */
function CameraBody({
  onClose,
  onCapture,
  onFallbackToFile,
}: {
  onClose: () => void;
  onCapture: (file: File) => void;
  onFallbackToFile: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("live");
  const [facingMode, setFacingMode] = useState<FacingMode>("environment");
  const [hasMulti, setHasMulti] = useState(false);
  const [ready, setReady] = useState(false); // first live frame is playing
  const [error, setError] = useState<{ message: string; offerFileFallback: boolean } | null>(null);
  const [still, setStill] = useState<Still | null>(null);
  // Bumped on Retake to force the acquisition effect to re-run and grab a fresh stream.
  const [startNonce, setStartNonce] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Keep the current still's URL in a ref so cleanup can revoke it without a stale closure.
  const stillUrlRef = useRef<string | null>(null);
  const titleId = useId();

  /** Stop and forget the active stream, and detach it from the <video>. Idempotent. */
  const teardownStream = useCallback(() => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // Acquire the live stream whenever we are in the live phase (initial mount, a camera switch,
  // or a Retake via startNonce). The cleanup stops exactly the stream this run started, so an
  // unmount, a switch or a phase change can never leave a camera on.
  useEffect(() => {
    if (phase !== "live") return;
    let cancelled = false;
    let localStream: MediaStream | null = null;
    setReady(false);
    setError(null);

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        if (cancelled) {
          // Modal closed / switched while awaiting: drop this stream, don't attach it.
          stopMediaStream(stream);
          return;
        }
        localStream = stream;
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          // play() can reject if interrupted; the loadeddata handler flips `ready` regardless.
          void video.play().catch(() => {});
        }
        // Only worth a front/back switch when the device actually has more than one camera.
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          if (!cancelled) setHasMulti(hasMultipleCameras(devices));
        } catch {
          /* enumerateDevices can be blocked; the switch just stays hidden. */
        }
      } catch (err) {
        if (!cancelled) {
          const name = err instanceof DOMException ? err.name : undefined;
          setError(cameraErrorMessage(name));
        }
      }
    })();

    return () => {
      cancelled = true;
      // Stop whichever stream this effect run owns (the awaited one, or an already-stored one).
      stopMediaStream(localStream ?? streamRef.current);
      if (streamRef.current === localStream) streamRef.current = null;
    };
    // facingMode + startNonce intentionally re-acquire; teardown handled in the returned cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, facingMode, startNonce]);

  // Revoke the review still's object URL when it changes or the body unmounts (no memory leak).
  useEffect(() => {
    stillUrlRef.current = still?.url ?? null;
    return () => {
      if (stillUrlRef.current) {
        URL.revokeObjectURL(stillUrlRef.current);
        stillUrlRef.current = null;
      }
    };
  }, [still]);

  /** Shutter: freeze the current frame, stop the camera immediately, move to review. */
  const onShutter = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !ready) return;
    try {
      // Draw BEFORE stopping the stream — the frame must exist first.
      const canvas = document.createElement("canvas");
      drawFrameToCanvas(video, canvas);
      teardownStream(); // camera light off the instant the still is captured
      const blob = await canvasToJpegBlob(canvas);
      setStill({ blob, url: URL.createObjectURL(blob) });
      setPhase("review");
    } catch {
      // Capture failed (e.g. no 2D context): treat as a camera error with a file fallback.
      setError({ message: "The photo could not be captured. You can choose a file instead.", offerFileFallback: true });
    }
  }, [ready, teardownStream]);

  /** Retake: discard the still and go back to a fresh live preview. */
  const onRetake = useCallback(() => {
    setStill(null); // its URL is revoked by the still effect's cleanup
    setPhase("live");
    setStartNonce((n) => n + 1); // force re-acquisition
  }, []);

  /** Use photo: hand the confirmed still to the existing upload path, then close. */
  const onUse = useCallback(() => {
    if (!still) return;
    const file = new File([still.blob], frameFileName(), { type: "image/jpeg" });
    onCapture(file);
    onClose();
  }, [still, onCapture, onClose]);

  const onSwitchCamera = useCallback(() => {
    teardownStream();
    setFacingMode((m) => nextFacingMode(m));
  }, [teardownStream]);

  // ----- error state: plain message + a way forward (file input), never a dead end -----
  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <CameraHeader titleId={titleId} title="Take photo" onClose={onClose} />
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <p role="alert" className="max-w-sm text-base text-ink-invert">
            {error.message}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {error.offerFileFallback && (
              <Button
                variant="primary"
                onClick={() => {
                  onClose();
                  onFallbackToFile();
                }}
              >
                Choose a file instead
              </Button>
            )}
            <Button variant="ghost" className="text-ink-invert hover:bg-ink-invert/10" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <CameraHeader
        titleId={titleId}
        title={phase === "review" ? "Review photo" : "Take photo"}
        onClose={onClose}
      />

      {/* Stage: the live preview and the review still occupy the same full-frame area. */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        {/* Live preview — kept mounted (hidden in review) so the <video> ref is always there. */}
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          aria-label="Live camera preview"
          onLoadedData={() => setReady(true)}
          className={`h-full w-full object-contain ${phase === "review" ? "hidden" : ""} ${
            facingMode === "user" ? "-scale-x-100" : ""
          }`}
        />

        {phase === "live" && !ready && (
          <p role="status" className="absolute inset-0 flex items-center justify-center text-sm text-ink-invert/80">
            Starting camera…
          </p>
        )}

        {phase === "review" && still && (
          // alt is empty: this is a just-taken preview the user is about to caption, not content.
          <img src={still.url} alt="" className="h-full w-full object-contain" />
        )}

        {/* Front/back switch — only when there is more than one camera, live phase only. */}
        {phase === "live" && hasMulti && (
          <button
            type="button"
            onClick={onSwitchCamera}
            aria-label="Switch camera"
            className="absolute right-3 top-3 flex h-12 w-12 items-center justify-center rounded-full bg-ink/50 text-ink-invert outline-none transition-colors hover:bg-ink/70 focus-visible:ring-2 focus-visible:ring-brand"
          >
            <SwitchCameraIcon />
          </button>
        )}
      </div>

      {/* Controls sit along the bottom for one-handed thumb reach; safe-area inset for notches. */}
      <div className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        {phase === "live" ? (
          <div className="flex items-center justify-center">
            {/* Large round shutter — brand violet, the one primary affordance here. */}
            <button
              type="button"
              onClick={() => void onShutter()}
              disabled={!ready}
              aria-label="Take photo"
              className="flex h-20 w-20 items-center justify-center rounded-full bg-brand text-ink-invert shadow-sheet outline-none transition-transform hover:bg-brand-strong focus-visible:ring-4 focus-visible:ring-brand/50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ShutterIcon />
            </button>
          </div>
        ) : (
          <div className="mx-auto flex max-w-md items-center justify-center gap-3">
            <Button
              variant="ghost"
              className="flex-1 text-ink-invert hover:bg-ink-invert/10"
              onClick={onRetake}
            >
              Retake
            </Button>
            <Button variant="primary" className="flex-1" onClick={onUse}>
              Use photo
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Shared header: the dialog title (for the accessible name) and a close button. */
function CameraHeader({
  titleId,
  title,
  onClose,
}: {
  titleId: string;
  title: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-4 py-2 text-ink-invert">
      <Dialog.Title id={titleId} className="text-sm font-medium">
        {title}
      </Dialog.Title>
      <Dialog.Close asChild>
        <Button
          variant="ghost"
          className="text-ink-invert hover:bg-ink-invert/10"
          aria-label="Close camera"
          onClick={onClose}
        >
          ✕
        </Button>
      </Dialog.Close>
    </div>
  );
}

/** Solid inner disc — the classic shutter glyph (inherits currentColor). */
function ShutterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-9 w-9" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
    </svg>
  );
}

/** Two arrows around a lens — the front/back switch glyph. */
function SwitchCameraIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <circle cx="12" cy="13" r="3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 8l2-2h6l2 2M17 6V4M7 18l-2 2M17 18l2 2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 11a8 8 0 0 1 4-4M20 15a8 8 0 0 1-4 4" />
    </svg>
  );
}
